import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, Clock, ChevronRight, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { articlesApi } from '../lib/api';
import { API } from '../lib/api';
import axios from 'axios';
import { useReadingProgress } from '../lib/useReadingProgress';
import { useTrackCTA } from '../lib/useTrackCTA';

const SERIES_ICONS = {
  'peptide-masterclass': { gradient: 'from-[#E63946]/20 to-[#E63946]/5', accent: '#E63946', badge: 'Peptides' },
  'recovery-peptides-deep-dive': { gradient: 'from-emerald-500/20 to-emerald-500/5', accent: '#22c55e', badge: 'Recovery' },
  'cognitive-enhancement-series': { gradient: 'from-violet-500/20 to-violet-500/5', accent: '#8b5cf6', badge: 'Brain' },
  'peptide-fundamentals': { gradient: 'from-amber-500/20 to-amber-500/5', accent: '#f59e0b', badge: 'Start Here' },
  'hyrox-race-ready': { gradient: 'from-[#FFD100]/20 to-[#FFD100]/5', accent: '#FFD100', badge: 'HYROX' },
  'biohacking-essentials': { gradient: 'from-cyan-500/20 to-cyan-500/5', accent: '#06b6d4', badge: 'Biohacking' },
  'body-composition-peptides': { gradient: 'from-orange-500/20 to-orange-500/5', accent: '#f97316', badge: 'Body Comp' },
  'longevity-anti-aging': { gradient: 'from-rose-400/20 to-rose-400/5', accent: '#fb7185', badge: 'Longevity' },
};

// ─── Series Browse Page ─────────────────────────────────────────
function SeriesBrowse() {
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const track = useTrackCTA();

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const res = await axios.get(`${API}/series`);
        setSeriesList(res.data || []);
      } catch (e) {
        console.error('Failed to fetch series', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSeries();
  }, []);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing">
          <div className="animate-pulse space-y-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-48 bg-zinc-800/50 rounded" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="series-browse-page">
      <div className="container-ninja section-spacing">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span
            className="inline-block px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#E63946] border border-[#E63946]/30 mb-6"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Learning Paths
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl text-white mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="series-page-title"
          >
            Article Series
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed">
            Curated reading paths that take you from fundamentals to advanced protocols. 
            Each series is designed to build on the previous article, giving you a structured learning experience.
          </p>
        </div>

        {/* Series Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="series-grid">
          {seriesList.map((series) => {
            const config = SERIES_ICONS[series.slug] || { gradient: 'from-zinc-500/20 to-zinc-500/5', accent: '#71717a', badge: 'Series' };
            const firstArticle = series.articles?.[0];

            return (
              <Link
                key={series.slug}
                to={`/series/${series.slug}`}
                className="group block border border-zinc-800 hover:border-zinc-700 bg-zinc-900/30 transition-all duration-300"
                data-testid={`series-card-${series.slug}`}
                onClick={() => track('series_browse_card', `/series/${series.slug}`)}
              >
                {/* Top accent bar */}
                <div className={`h-1 bg-gradient-to-r ${config.gradient}`} style={{ background: `linear-gradient(to right, ${config.accent}40, transparent)` }} />

                <div className="p-6">
                  {/* Badge + Count */}
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className="inline-block px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold"
                      style={{ color: config.accent, backgroundColor: `${config.accent}15`, border: `1px solid ${config.accent}30` }}
                    >
                      {config.badge}
                    </span>
                    <span className="text-zinc-600 text-xs">
                      {series.article_count} article{series.article_count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Title */}
                  <h2
                    className="text-white text-xl mb-2 group-hover:text-zinc-200 transition-colors"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="series-card-title"
                  >
                    {series.name}
                  </h2>

                  {/* Description */}
                  <p className="text-zinc-500 text-sm leading-relaxed mb-5 line-clamp-2">
                    {series.description}
                  </p>

                  {/* Progress bar */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-[3px] bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: '0%', backgroundColor: config.accent }} />
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400 group-hover:text-white transition-colors flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Start Reading
                    </span>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors group-hover:translate-x-1 duration-300" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </main>
  );
}

// ─── Series Detail Page ─────────────────────────────────────────
function SeriesDetail() {
  const { slug } = useParams();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getProgress } = useReadingProgress();
  const track = useTrackCTA();

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const res = await axios.get(`${API}/series/${slug}`);
        setSeries(res.data);
      } catch (e) {
        console.error('Failed to fetch series', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSeries();
  }, [slug]);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-zinc-800/50 w-1/3 rounded" />
            <div className="h-6 bg-zinc-800/50 w-2/3 rounded" />
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-zinc-800/50 rounded" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (!series) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing text-center">
          <h1 className="text-white text-3xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Series Not Found
          </h1>
          <Link to="/series" className="btn-ninja inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Browse All Series
          </Link>
        </div>
      </main>
    );
  }

  const config = SERIES_ICONS[series.slug] || { gradient: 'from-zinc-500/20 to-zinc-500/5', accent: '#71717a', badge: 'Series' };
  const articles = series.articles_data || [];
  const completedCount = articles.filter(a => {
    const p = getProgress(a.slug);
    return p && p.progress >= 90;
  }).length;

  return (
    <main className="pt-20 min-h-screen" data-testid="series-detail-page">
      <div className="container-ninja section-spacing">
        {/* Back */}
        <Link
          to="/series"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-series"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            All Series
          </span>
        </Link>

        {/* Header */}
        <div className="max-w-3xl mb-12">
          <span
            className="inline-block px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold mb-4"
            style={{ color: config.accent, backgroundColor: `${config.accent}15`, border: `1px solid ${config.accent}30` }}
          >
            {config.badge} Series
          </span>
          <h1
            className="text-4xl sm:text-5xl text-white mb-3"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="series-detail-title"
          >
            {series.name}
          </h1>
          {series.description && (
            <p className="text-zinc-400 text-base leading-relaxed mb-6">{series.description}</p>
          )}

          {/* Overall progress */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-[4px] bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: articles.length > 0 ? `${(completedCount / articles.length) * 100}%` : '0%',
                  backgroundColor: config.accent,
                }}
                data-testid="series-overall-progress"
              />
            </div>
            <span className="text-zinc-500 text-sm whitespace-nowrap">
              {completedCount} of {articles.length} completed
            </span>
          </div>
        </div>

        {/* Article List */}
        <div className="max-w-3xl space-y-4" data-testid="series-articles-list">
          {articles.map((article, idx) => {
            const progress = getProgress(article.slug);
            const isCompleted = progress && progress.progress >= 90;
            const isStarted = progress && progress.progress > 0 && progress.progress < 90;

            return (
              <Link
                key={article.slug}
                to={`/blogs/article/${article.slug}`}
                className="group flex gap-5 p-4 border border-zinc-800 hover:border-zinc-700 bg-zinc-900/30 transition-all duration-300"
                data-testid={`series-article-${idx}`}
              >
                {/* Number / Check */}
                <div className="shrink-0 flex items-start pt-1">
                  {isCompleted ? (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${config.accent}20` }}
                    >
                      <CheckCircle2 className="w-5 h-5" style={{ color: config.accent }} />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-500 text-sm font-medium group-hover:border-zinc-500 transition-colors">
                      {idx + 1}
                    </div>
                  )}
                </div>

                {/* Thumbnail */}
                {article.featured_image && (
                  <div className="shrink-0 w-24 h-16 overflow-hidden bg-zinc-800 hidden sm:block">
                    <img
                      src={article.featured_image}
                      alt=""
                      className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white text-sm sm:text-base font-medium group-hover:text-zinc-200 transition-colors leading-tight mb-1 line-clamp-2">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-3 text-zinc-600 text-xs">
                    {article.read_time && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {article.read_time} min
                      </span>
                    )}
                    {isStarted && (
                      <span style={{ color: config.accent }} className="text-[10px] uppercase tracking-wider font-medium">
                        In Progress
                      </span>
                    )}
                    {isCompleted && (
                      <span style={{ color: config.accent }} className="text-[10px] uppercase tracking-wider font-medium">
                        Completed
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <div className="shrink-0 flex items-center">
                  <ArrowRight className="w-4 h-4 text-zinc-700 group-hover:text-white transition-colors group-hover:translate-x-1 duration-300" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Start CTA */}
        {articles.length > 0 && (
          <div className="max-w-3xl mt-10">
            <Link
              to={`/blogs/article/${articles[0].slug}`}
              className="inline-flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-wider transition-all duration-300 hover:opacity-90"
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                backgroundColor: config.accent,
                color: ['#FFD100', '#f59e0b'].includes(config.accent) ? '#000' : '#fff',
              }}
              data-testid="series-start-reading-btn"
              onClick={() => track('series_start_reading', `/blogs/article/${articles[0].slug}`)}
            >
              <BookOpen className="w-4 h-4" />
              {completedCount > 0 ? 'Continue Reading' : 'Start Series'}
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}

// ─── Router Export ───────────────────────────────────────────────
export default function SeriesPage() {
  const { slug } = useParams();
  return slug ? <SeriesDetail /> : <SeriesBrowse />;
}
